import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { injectMutation, injectQuery, injectQueryClient, QueryObserverResult } from '@ngneat/query'
import { map, Observable, tap } from 'rxjs'
import { AdminCallDetails, CallMedium, MinutesStyling } from './calls.service'
import { DownloadsService } from './downloads.service'
import { ListParameterQuery } from './list-query-options'
import { queryKeys } from './query-keys'
import { ToastService } from './toast.service'
import { AdminListUser, User } from './users.service'
import { generateHeadersWithTimezone } from './utils'

export type EnterpriseOidcProviderPlatform = 'AZURE' | 'OKTA'
export type EnterpriseOidcProviderType = 'AUTH' | 'CALENDAR' | 'AUTH_AND_CALENDAR'
export type GuestOrganization = {
  id: number
  name: string
  domains: string[]
}

export type EnterpriseOidcProvider = {
  platform: EnterpriseOidcProviderPlatform
  type: EnterpriseOidcProviderType
  keycloakHint: string
}

export type Organization = {
  id: number
  name: string
  canBeSuper: boolean
  shareUrlOptOut: boolean
  outputLanguage: string
  allowTranscriptDownload: boolean
  allowVideoDownload: boolean
  allowMinutesDownload: boolean
  allowOutlineDownload: boolean
  allowManualCallCreation: boolean
  allowThirdPartySharing: boolean
  allowCallUpload: boolean
  isIntercomEnabled: boolean
  isSharingEnabled: boolean
  isCalendarSynchronizationEnabled: boolean
  allowSignUp: boolean
  llmModel: string
  recordingType: 'VIDEO' | 'AUDIO'
  retentionPeriodInDaysForMedia: number | null
  retentionPeriodInDaysForDocuments: number | null
  retentionPeriodInDaysForTranscript: number | null
  retentionPeriodInDaysForCall: number | null
  softDeletionPeriodInDays: number
  assistantDisplayName: string | null
  domains: string[]
  enterpriseOidcProviders: EnterpriseOidcProvider[]
  contactEmails: string[]
  organizationType: string
  deactivated: boolean
  createdAt: Date
  defaultCallMedium: CallMedium
  sendSoftDeletionWarningEmail: boolean
  sendSoftDeletionConfirmationEmail: boolean
  sendHardDeletionConfirmationEmail: boolean
  sendAssistantAccessDeniedEmail: boolean
  sendUpcomingCallReminderEmail: boolean | null
  sendWeeklyDigestEmail: boolean
  joinMeetingInternal: boolean
  joinMeetingExternal: boolean
  freezeJoinSettings: boolean
  defaultCallProfileId: number | null
  context: string
  scimKey: string | null
  generateMinutesWithCitations: boolean
} & MinutesStyling

export type OrganizationDetails = Organization & {
  users: User[]
}

export type OrganizationToBeCreated = Omit<Organization, 'id' | 'createdAt'>

export type OrganizationQueryResult = {
  list: (Organization & { userCount: number })[]
  total: number
}

export type OrganizationUpdateData = {
  organization: Organization
}

export type OrganizationCreateData = {
  organization: OrganizationToBeCreated
}

export type UsageEntry = {
  totalCallDurationInSeconds: number
  totalCallCount: number
}

export type OrganizationUsage = {
  label: string
  usage: UsageEntry
}[]

export type AdminOrganizationDetails = Pick<
  Organization,
  | 'id'
  | 'name'
  | 'createdAt'
  | 'llmModel'
  | 'retentionPeriodInDaysForMedia'
  | 'retentionPeriodInDaysForDocuments'
  | 'retentionPeriodInDaysForTranscript'
  | 'retentionPeriodInDaysForCall'
  | 'softDeletionPeriodInDays'
  | 'defaultCallMedium'
  | 'enterpriseOidcProviders'
  | 'generateMinutesWithCitations'
> &
  MinutesStyling & {
    pastCalls: AdminCallDetails[]
    upcomingCalls: AdminCallDetails[]
    erroredCalls: AdminCallDetails[]
    usage: OrganizationUsage
    users: AdminListUser[]
    guestOrganization: GuestOrganization
  }

export function getMinuteStylesOfOrganization(organization: Organization): MinutesStyling {
  return {
    minutesVerboseness: organization.minutesVerboseness,
    minutesPassive: organization.minutesPassive,
    minutesAnonymization: organization.minutesAnonymization,
    minutesAttribution: organization.minutesAttribution,
    minutesNameFormat: organization.minutesNameFormat,
    minutesDateFormat: organization.minutesDateFormat,
    minutesSpelling: organization.minutesSpelling,
    minutesTimeFormat: organization.minutesTimeFormat,
  }
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  router = inject(Router)
  downloadsService = inject(DownloadsService)

  getAllOrganizations(query: string | null = null, sortField: string | null = null, sortDirection: 'asc' | 'desc' | null = null) {
    const params: { query?: string; sortField?: string; sortDirection?: 'asc' | 'desc' } = {}
    if (query !== null) {
      params.query = query
    }
    if (sortField !== null) {
      params.sortField = sortField
    }
    if (sortDirection !== null) {
      params.sortDirection = sortDirection
    }
    return this.query({
      queryKey: [...queryKeys.organizations.all, query, sortField, sortDirection],
      queryFn: () =>
        this.httpClient.get<(Organization & { userCount: number })[]>('/api/organizations', {
          params,
        }),
    }).result$
  }

  getOrganizations(page: number, pageSize: number, sortField?: string, sortDirection?: 'asc' | 'desc') {
    const params: ListParameterQuery = { page, pageSize }
    if (sortField) {
      params.sortField = sortField
      params.sortDirection = sortDirection
    }

    return this.query({
      queryKey: queryKeys.organizations.base,
      queryFn: () => this.httpClient.get<OrganizationQueryResult>('/api/organizations/admin', { params }),
    }).result$
  }

  getOrganization(id: number, receiveUpdates = false) {
    return this.query({
      queryKey: queryKeys.organizations.byId(id),
      queryFn: () => this.httpClient.get<OrganizationDetails>(`/api/organizations/${id}`),
      refetchOnMount: receiveUpdates,
      refetchOnReconnect: receiveUpdates,
      refetchOnWindowFocus: receiveUpdates,
    }).result$
  }

  createOrganization() {
    return this.mutation({
      mutationFn: (organizationCreateDto: OrganizationCreateData) => this.httpClient.post<Organization>('/api/organizations', organizationCreateDto),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base }),
      onError: () => {
        this.toastService.show('ORGANIZATIONS.CREATE_FAILED')
      },
    })
  }

  updateOrganization() {
    return this.mutation({
      mutationFn: (organizationUpdateDto: OrganizationUpdateData) =>
        this.httpClient.patch<Organization>(`/api/organizations/${organizationUpdateDto.organization.id}`, organizationUpdateDto),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base }),
      onError: () => {
        this.toastService.show('ORGANIZATIONS.EDIT_FAILED')
      },
    })
  }

  deleteOrganization() {
    return this.mutation({
      mutationFn: (id: number) => this.httpClient.delete<void>(`/api/organizations/${id}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base })
      },
      onError: () => {
        this.toastService.show('ORGANIZATIONS.ADMIN.DELETE_FAILED')
      },
    })
  }

  getAdminViewToOrganization(id: number): Observable<QueryObserverResult<AdminOrganizationDetails>> {
    return this.query({
      queryKey: queryKeys.organizations.byId(id),
      queryFn: () => this.httpClient.get<AdminOrganizationDetails>(`/api/organizations/${id}/admin`),
      refetchInterval: 30000,
    }).result$
  }

  downloadUsageReport(organization: AdminOrganizationDetails, fromDate: Date, toDate: Date): Observable<void> {
    const fileName = `usage-report-${organization.name}-${fromDate.toISOString()}-${toDate.toISOString()}.csv`
    return this.httpClient
      .get(`/api/organizations/${organization.id}/usage-report`, {
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
        params: {
          startDate: fromDate.toISOString(),
          endDate: toDate.toISOString(),
        },
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, 'csv')
        }),
        map(() => void 0)
      )
  }
}
